import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { DownloadSimple, BookOpen, Broadcast, DeviceMobile, FileText, ClosedCaptioning, MagnifyingGlass, VideoCamera } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api from "../lib/api";

const DOCS = [
  { key: "manual", title: "Manuale Admin", file: "MANUALE_ADMIN_IT.md", icon: BookOpen },
  { key: "live", title: "Guida Live", file: "GUIDA_LIVE_STEP_BY_STEP.md", icon: Broadcast },
  { key: "gopro", title: "GoPro (Registrazione + Live)", file: "GUIDA_GOPRO_LIVE.md", icon: VideoCamera },
  { key: "aruba", title: "Guida Aruba DNS", file: "GUIDA_ARUBA_DEPLOY.md", icon: Broadcast },
  { key: "pwa", title: "PWA & Store", file: "GUIDA_PWA_STORE.md", icon: DeviceMobile },
  { key: "transcripts", title: "Trascrizioni Video", file: "GUIDA_TRASCRIZIONI.md", icon: ClosedCaptioning },
  { key: "seo", title: "Migrazione SEO", file: "GUIDA_MIGRAZIONE_SEO.md", icon: MagnifyingGlass },
  { key: "specs", title: "Specifiche Tecniche", file: "SPECIFICHE_TECNICHE_WBA.md", icon: FileText },
];

export default function AdminHelp() {
  const [active, setActive] = useState("manual");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/docs/${active}`, { responseType: "text", transformResponse: (v) => v }).then((res) => {
      setContent(res.data);
    }).catch(() => setContent("# Errore\nImpossibile caricare il documento.")).finally(() => setLoading(false));
  }, [active]);

  const download = async (key, filename) => {
    try {
      const res = await api.get(`/admin/docs/${key}?download=1`, { responseType: "blob", transformResponse: (v) => v });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/markdown" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* toast handled globally */ }
  };

  return (
    <div className="min-h-screen bg-ink-900 pb-24">
      <div className="mx-auto max-w-6xl px-6 pt-32 md:px-10">
        <Link to="/admin" className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan hover:text-white">← Admin</Link>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-6 font-head text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">
          Guida Admin
        </motion.h1>
        <p className="mt-2 max-w-2xl font-body text-slate-400">Istruzioni operative complete per gestire il sito. Puoi anche scaricare i documenti in formato Markdown.</p>

        {/* Doc selector */}
        <div className="mt-10 flex flex-wrap gap-3 border-y border-white/10 py-5">
          {DOCS.map((d) => {
            const Icon = d.icon;
            const on = active === d.key;
            return (
              <button
                key={d.key}
                data-testid={`doc-tab-${d.key}`}
                onClick={() => setActive(d.key)}
                className="flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors"
                style={{ borderColor: on ? "#00F0FF" : "rgba(255,255,255,0.15)", backgroundColor: on ? "rgba(0,240,255,0.12)" : "transparent", color: on ? "#00F0FF" : "#94A3B8" }}
              >
                <Icon size={14} weight="bold" /> {d.title}
              </button>
            );
          })}
          <div className="ml-auto">
            {DOCS.filter((d) => d.key === active).map((d) => (
              <button
                key={d.key}
                data-testid={`doc-download-${d.key}`}
                onClick={() => download(d.key, d.file)}
                className="flex items-center gap-2 border border-cyan/50 bg-cyan/5 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-cyan transition-colors hover:bg-cyan hover:text-ink-900"
              >
                <DownloadSimple size={14} weight="bold" /> Scarica {d.file}
              </button>
            ))}
          </div>
        </div>

        {/* Markdown content */}
        <div data-testid="doc-content" className="prose prose-invert mt-10 max-w-none font-body">
          {loading ? (
            <p className="font-mono text-sm text-slate-500">Loading…</p>
          ) : (
            <ReactMarkdown
              components={{
                h1: (p) => <h1 className="mt-10 font-head text-3xl font-bold uppercase tracking-tight text-white first:mt-0" {...p} />,
                h2: (p) => <h2 className="mt-10 border-b border-white/10 pb-2 font-head text-2xl font-bold uppercase tracking-tight text-cyan" {...p} />,
                h3: (p) => <h3 className="mt-6 font-head text-lg font-bold uppercase tracking-widest text-white" {...p} />,
                p: (p) => <p className="mt-3 text-base leading-relaxed text-slate-300" {...p} />,
                ul: (p) => <ul className="mt-3 list-disc space-y-1.5 pl-6 text-slate-300" {...p} />,
                ol: (p) => <ol className="mt-3 list-decimal space-y-1.5 pl-6 text-slate-300" {...p} />,
                li: (p) => <li className="text-base leading-relaxed" {...p} />,
                strong: (p) => <strong className="font-bold text-white" {...p} />,
                code: (p) => <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[13px] text-cyan" {...p} />,
                a: (p) => <a className="text-cyan underline underline-offset-4 hover:text-white" target="_blank" rel="noreferrer" {...p} />,
                blockquote: (p) => <blockquote className="mt-4 border-l-2 border-cyan/60 pl-4 italic text-slate-400" {...p} />,
                table: (p) => <div className="mt-4 overflow-x-auto"><table className="min-w-full border-collapse text-left text-sm text-slate-300" {...p} /></div>,
                th: (p) => <th className="border-b border-white/20 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-cyan" {...p} />,
                td: (p) => <td className="border-b border-white/5 px-3 py-2" {...p} />,
                hr: () => <hr className="mt-10 border-white/10" />,
              }}
            >
              {content}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
}
